import { useState } from "react";
import { GamesList } from "./GamesList";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/20/solid";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { GameView } from "./GameView";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";
import { Login } from "./Login";
import { Signup } from "./Signup";
import { gamesAPI } from "./api";
import { IOSInstallPrompt } from "./components/IOSInstallPrompt";

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

export const NavShell = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const [isCreatingGame, setIsCreatingGame] = useState(false);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <GamesList />,
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/signup",
      element: <Signup />,
    },
    {
      path: "/game/:id",
      element: <GameView />,
    },
  ]);

  const navigation = [
    { name: "Dashboard", href: "/", current: true },
  ];

  const handleCreateGame = async () => {
    if (!user?.id || isCreatingGame) return;

    try {
      setIsCreatingGame(true);
      const game = await gamesAPI.createGame(user.id);
      window.location.href = `/game/${game.id}`;
    } catch (error) {
      console.error('Failed to create game:', error);
      alert('Failed to create game. Please try again.');
    } finally {
      setIsCreatingGame(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="bg-gray-800 pb-32">
        <Disclosure as="nav" className="bg-gray-800">
          {({ open }) => (
            <>
              <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="border-b border-orange-800">
                  <div className="flex h-16 items-center justify-between px-4 sm:px-0">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <img
                          className="h-12 w-12"
                          src="/logo.svg"
                          alt="Your Company"
                        />
                      </div>
                      {isSignedIn && (
                        <div className="hidden md:block">
                          <div className="ml-10 flex items-baseline space-x-4">
                            {navigation.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className={classNames(
                                  item.current
                                    ? "bg-gray-900 text-white"
                                    : "text-gray-300 hover:bg-gray-700 hover:text-white",
                                  "rounded-md px-3 py-2 text-sm font-medium"
                                )}
                                aria-current={
                                  item.current ? "page" : undefined
                                }
                              >
                                {item.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {isSignedIn && (
                      <>
                        <div className="hidden md:block">
                          <div className="ml-4 flex items-center md:ml-6">
                            <div className="relative ml-3">
                              <UserButton
                                appearance={{
                                  elements: {
                                    avatarBox: "h-8 w-8",
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="-mr-2 flex md:hidden">
                          <DisclosureButton className="relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                            <span className="absolute -inset-0.5" />
                            <span className="sr-only">Open main menu</span>
                            {open ? (
                              <XMarkIcon
                                className="block h-6 w-6"
                                aria-hidden="true"
                              />
                            ) : (
                              <Bars3Icon
                                className="block h-6 w-6"
                                aria-hidden="true"
                              />
                            )}
                          </DisclosureButton>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {isSignedIn && (
                <DisclosurePanel className="border-b border-gray-700 md:hidden">
                  <div className="space-y-1 px-2 py-3 sm:px-3">
                    {navigation.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as="a"
                        href={item.href}
                        className={classNames(
                          item.current
                            ? "bg-gray-900 text-white"
                            : "text-gray-300 hover:bg-gray-700 hover:text-white",
                          "block rounded-md px-3 py-2 text-base font-medium"
                        )}
                        aria-current={item.current ? "page" : undefined}
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 pb-3 pt-4">
                    <div className="flex items-center px-5">
                      <div className="flex-shrink-0">
                        <UserButton
                          appearance={{
                            elements: {
                              avatarBox: "h-10 w-10",
                            },
                          }}
                        />
                      </div>
                      <div className="ml-3">
                        <div className="text-base font-medium leading-none text-white">
                          {user?.username || user?.firstName || "User"}
                        </div>
                        {user?.primaryEmailAddress?.emailAddress && (
                          <div className="text-sm font-medium leading-none text-gray-400 mt-1">
                            {user.primaryEmailAddress.emailAddress}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </DisclosurePanel>
              )}
            </>
          )}
        </Disclosure>
        <header className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex justify-between">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              Table Footy
            </h1>
            {isSignedIn && (
              <div className="text-white content-center">
                <button
                  onClick={handleCreateGame}
                  disabled={isCreatingGame}
                  type="button"
                  className="inline-flex items-center gap-x-2 rounded-md bg-orange-700 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                  {isCreatingGame ? 'Creating...' : 'New Game'}
                </button>
              </div>
            )}
          </div>
        </header>
      </div>

      <main className="-mt-32">
        <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
          <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6">
            {isSignedIn ? (
              <RouterProvider router={router} />
            ) : (
              <Login />
            )}
          </div>
        </div>
      </main>
      <IOSInstallPrompt />
    </div>
  );
};
